import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Menu, X } from "lucide-react";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { useI18n } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

export default function MarketingHeader() {
  const { t } = useI18n();
  const { isAuthenticated } = useAuth();
  const [open, setOpen] = useState(false);

  const navItems = [
    { key: "features", target: "why" },
    { key: "philosophy", target: "philosophy" },
    { key: "faq", target: "faq" },
  ];

  return (
    <header
      className="sticky top-0 z-40 backdrop-blur-xl"
      style={{
        backgroundColor: "hsl(var(--nvl-bg) / 0.82)",
        borderBottom: "1px solid hsl(var(--nvl-border))",
      }}
      data-testid="marketing-header"
    >
      <div className="nvl-container flex items-center justify-between h-16">
        <Link to="/" data-testid="marketing-logo" className="flex items-center gap-2">
          <span className="font-display text-xl tracking-tight">NO VAX LOVE</span>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {navItems.map((n) => (
            <button
              key={n.key}
              onClick={() => scrollTo(n.target)}
              data-testid={`nav-${n.key}`}
              className="text-sm font-medium hover:opacity-70"
              style={{ transition: "opacity 180ms" }}
            >
              {t(`nav.${n.key}`)}
            </button>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <LanguageSwitcher />
          {isAuthenticated ? (
            <Link to="/app/discover" data-testid="nav-open-app" className="nvl-btn nvl-btn-ink">
              {t("app.nav.discover")}
            </Link>
          ) : (
            <>
              <Link to="/login" data-testid="nav-login" className="nvl-btn nvl-btn-outline">
                {t("nav.login")}
              </Link>
              <Link to="/register" data-testid="nav-register" className="nvl-btn nvl-btn-primary">
                {t("nav.register")}
              </Link>
            </>
          )}
        </div>

        <button
          type="button"
          className="md:hidden p-2"
          onClick={() => setOpen((v) => !v)}
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      {open && (
        <div
          className="md:hidden border-t"
          style={{ borderColor: "hsl(var(--nvl-border))" }}
          data-testid="mobile-menu"
        >
          <div className="nvl-container py-4 flex flex-col gap-4">
            {navItems.map((n) => (
              <button
                key={n.key}
                onClick={() => {
                  scrollTo(n.target);
                  setOpen(false);
                }}
                className="text-left text-sm font-medium"
                data-testid={`nav-${n.key}-mobile`}
              >
                {t(`nav.${n.key}`)}
              </button>
            ))}
            <div className="flex items-center gap-2 pt-2">
              <LanguageSwitcher />
            </div>
            <div className="flex flex-col gap-2 pt-2">
              {isAuthenticated ? (
                <Link to="/app/discover" onClick={() => setOpen(false)} className="nvl-btn nvl-btn-ink">
                  {t("app.nav.discover")}
                </Link>
              ) : (
                <>
                  <Link to="/login" onClick={() => setOpen(false)} className="nvl-btn nvl-btn-outline" data-testid="nav-login-mobile">
                    {t("nav.login")}
                  </Link>
                  <Link to="/register" onClick={() => setOpen(false)} className="nvl-btn nvl-btn-primary" data-testid="nav-register-mobile">
                    {t("nav.register")}
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
